import "./opentelemetry"; // oxlint-disable-line no-unassigned-import, no-duplicates
import "./sentry"; // oxlint-disable-line no-unassigned-import
import "@/styles/main.css";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { context } from "@opentelemetry/api";
import * as Sentry from "@sentry/react"; // oxlint-disable-line no-namespace
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18next from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import Fluent from "i18next-fluent";
import FluentBackend from "i18next-fluent-backend";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { RouterProvider } from "react-router";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RenderError } from "@/components/RenderError";
import { ServiceWorkerProvider } from "@/components/ServiceWorkerProvider";
import { router } from "@/router";

import { getParentContext } from "./opentelemetry";

if (import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
  setOptions({ key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY });
  void importLibrary("maps");
  void importLibrary("marker");
  void importLibrary("places");
}

const i18n = i18next
  .createInstance({
    fallbackLng: "en",
    supportedLngs: ["en"],

    defaultNS: "translations",
    ns: ["translations", "errors", "rich-text"],

    debug: import.meta.env.DEV,
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
  })
  .use(Fluent)
  .use(FluentBackend)
  .use(I18nextBrowserLanguageDetector);

void i18n.init((error: unknown) => {
  if (error) console.error(error);
});

context.with(getParentContext(), () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
        retryOnMount: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        gcTime: 5 * 60 * 1000,
      },
      mutations: {
        networkMode: "always",
        retry: 0,
        gcTime: 0,
      },
    },
  });

  const root = createRoot(document.querySelector("#root")!, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });

  root.render(
    <StrictMode>
      <meta property="og:site_name" content="Bale" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${import.meta.env.VITE_SERVER_URL}/turtle.svg`} />
      <meta property="og:url" content={window.location.href} />

      <ErrorBoundary fallback={RenderError}>
        <Suspense
          fallback={
            <div className="flex h-screen w-screen items-center justify-center overflow-hidden bg-grass">
              <img src="/turtle.svg" alt="Loading turtle" />
            </div>
          }
        >
          <I18nextProvider i18n={i18n} defaultNS="translations">
            <QueryClientProvider client={queryClient}>
              <ServiceWorkerProvider>
                <RouterProvider router={router} />
              </ServiceWorkerProvider>
            </QueryClientProvider>
          </I18nextProvider>
        </Suspense>
      </ErrorBoundary>
    </StrictMode>,
  );
});
